import { SignIn, SignUp, useAuth } from "@clerk/react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Radio, Network, ArrowLeft } from "lucide-react";
import { MARKETING_ORIGIN } from "@/lib/hosts";
import { commandClerkAppearance } from "@/lib/clerkAppearance";
import { CommandBootScreen } from "@/components/CommandBootScreen";

/** Re-export for pages that still import appearance from CommandGate */
export { commandClerkAppearance };

/**
 * Branded gate at command.opengateways.xyz before the shell.
 *
 * Path routing (not hash) so OAuth / social SSO / X can return to a real URL
 * (/sign-in, /sign-up, /sso-callback) instead of getting stuck on #/….
 */
export function CommandGate() {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initialMode: "sign-in" | "sign-up" =
    location.pathname.startsWith("/sign-up") ? "sign-up" : "sign-in";
  const [mode, setMode] = useState<"sign-in" | "sign-up">(initialMode);

  // Keep UI tab in sync when user hits /sign-up or /sign-in directly
  useEffect(() => {
    if (location.pathname.startsWith("/sign-up")) setMode("sign-up");
    else if (location.pathname.startsWith("/sign-in")) setMode("sign-in");
  }, [location.pathname]);

  if (!isLoaded) {
    return <CommandBootScreen detail="Loading your workspace…" />;
  }

  // Preserve query (e.g. ?upgrade=team) through auth + SSO return
  const afterAuth = `/${location.search}`;

  if (isSignedIn) {
    return <Navigate to={afterAuth} replace />;
  }

  const switchMode = (next: "sign-in" | "sign-up") => {
    setMode(next);
    navigate(
      {
        pathname: next === "sign-up" ? "/sign-up" : "/sign-in",
        search: location.search,
      },
      { replace: true }
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-og-ink text-white">
      {/* Ambient */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-0 h-[40rem] w-[40rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-[120px]" />
        <div className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-violet-600/15 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl flex-col lg:flex-row">
        {/* Brand panel */}
        <aside className="flex flex-1 flex-col justify-between px-6 py-8 md:px-10 lg:py-12">
          <a
            href={MARKETING_ORIGIN}
            className="inline-flex w-fit items-center gap-2 text-sm text-zinc-400 transition hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            opengateways.xyz
          </a>

          <div className="my-12 max-w-md lg:my-0">
            <div className="mb-6 flex items-center gap-3">
              <img
                src="/og-logo.png"
                alt=""
                className="h-12 w-12 rounded-2xl shadow-xl shadow-orange-500/30"
                width={48}
                height={48}
              />
              <div>
                <p className="text-lg font-semibold tracking-tight">
                  OpenGateway
                </p>
                <p className="text-xs font-medium uppercase tracking-[0.18em] text-orange-400/90">
                  Command
                </p>
              </div>
            </div>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Sign in to your radio.
            </h1>
            <p className="mt-3 text-base leading-relaxed text-zinc-400">
              Humans on the left. Agents on every machine. One room when you
              need the whole team. Email or SSO (Google, X, etc.).
            </p>

            <ul className="mt-8 space-y-3 text-sm text-zinc-400">
              <li className="flex items-start gap-2.5">
                <Radio className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />
                Presence that shows who’s actually listening
              </li>
              <li className="flex items-start gap-2.5">
                <Network className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" />
                Many agents across many machines — not one chat box
              </li>
            </ul>
          </div>

          <p className="hidden text-[11px] text-zinc-600 lg:block">
            Secure sign-in · Your workspace on command.opengateways.xyz
          </p>
        </aside>

        {/* Auth card */}
        <section className="flex flex-1 items-center justify-center px-4 pb-12 pt-2 lg:px-10 lg:py-12">
          <div className="w-full max-w-[400px]">
            <div className="mb-5 flex rounded-full border border-white/10 bg-white/[0.03] p-1">
              <button
                type="button"
                onClick={() => switchMode("sign-in")}
                className={`flex-1 rounded-full py-2 text-sm font-medium transition ${
                  mode === "sign-in"
                    ? "bg-white/10 text-white shadow"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => switchMode("sign-up")}
                className={`flex-1 rounded-full py-2 text-sm font-medium transition ${
                  mode === "sign-up"
                    ? "bg-white/10 text-white shadow"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                Create account
              </button>
            </div>

            {mode === "sign-in" ? (
              <SignIn
                routing="path"
                path="/sign-in"
                appearance={commandClerkAppearance}
                signUpUrl={
                  location.search ? `/sign-up${location.search}` : "/sign-up"
                }
                fallbackRedirectUrl={afterAuth}
                forceRedirectUrl={afterAuth}
              />
            ) : (
              <SignUp
                routing="path"
                path="/sign-up"
                appearance={commandClerkAppearance}
                signInUrl={
                  location.search ? `/sign-in${location.search}` : "/sign-in"
                }
                fallbackRedirectUrl={afterAuth}
                forceRedirectUrl={afterAuth}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
